import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import { IconButton, Tooltip } from '@mui/material';
import { serverTimestamp } from 'firebase/firestore';
import React, { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ReactionType } from '../../../firebase/firebase.types';
import useToast from '../../../hooks/useToast';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { updateCard } from '../../../redux/trip/trip.slice';
import { TripCard } from '../../../redux/trip/trip.types';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import { getReactionDescription } from '../../../utils/reaction-labels';
import { getReactionEmoji, REACTION_TYPES } from '../../../utils/reaction-utils';
import ActionMenu from '../ActionMenu/ActionMenu';
import './CardActionBar.scss';

interface CardActionBarProps {
  card: TripCard;
  tripId: string;
  canEdit?: boolean;
  isVisible?: boolean;
  isLarge?: boolean;
  onRename?: () => void;
  onChangeCoverPhoto?: () => void;
  onDelete?: (event: MouseEvent<EventTarget>) => void;
}

function CardActionBar({
  card,
  tripId,
  canEdit = false,
  isVisible = false,
  isLarge = false,
  onRename,
  onChangeCoverPhoto,
  onDelete,
}: CardActionBarProps) {
  const dispatch = useAppDispatch();
  const { warning: toastWarning } = useToast();
  const currentUser = useAppSelector(selectCurrentUser);
  const reactionContainerRef = useRef<HTMLDivElement>(null);
  const [showReactionSelector, setShowReactionSelector] = useState(false);

  // Calculate user's current reaction
  const userCurrentReaction = useMemo(() => {
    if (currentUser?.uid && card.reactions) {
      return card.reactions[currentUser.uid] || null;
    }
    return null;
  }, [card.reactions, currentUser?.uid]);

  // const handleBookmarkClick = (event: MouseEvent<HTMLElement>) => {
  //   event.stopPropagation();
  //   if (process.env.NODE_ENV === 'development') {
  //     // biome-ignore lint/suspicious/noConsole: Development logging for placeholder feature
  //     console.log('Bookmark button clicked for card:', card.name);
  //   }
  //   setSnackbarMessage?.({
  //     severity: 'info',
  //     text: 'Bookmark feature coming soon! 📖',
  //   });
  // };

  const onReactionSelect = (reaction: ReactionType | null): void => {
    if (!canEdit) {
      toastWarning('You must be a collaborator of this trip in order to vote.');
      return;
    }

    if (currentUser?.uid) {
      const reactions = { ...(card.reactions || {}) };

      if (reaction === null) {
        if (reactions[currentUser.uid]) {
          delete reactions[currentUser.uid];
        }
      } else {
        reactions[currentUser.uid] = {
          type: reaction,
          timestamp: serverTimestamp(),
          email: currentUser.email,
        };
      }

      dispatch(
        updateCard({
          tripId,
          cardId: card.id,
          field: 'reactions',
          value: reactions,
        })
      );
    }
  };

  const handleReactionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReactionSelector((prev) => !prev);
  };

  const handleReactionSelect = (reaction: ReactionType, e: React.MouseEvent): void => {
    e.stopPropagation();
    const isCurrentlySelected = userCurrentReaction?.type === reaction;
    onReactionSelect(isCurrentlySelected ? null : reaction);
    setShowReactionSelector(false);
  };

  const isSelected = (reaction: ReactionType) => userCurrentReaction?.type === reaction;

  // Close the reaction selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      if (
        reactionContainerRef.current &&
        !reactionContainerRef.current.contains(e.target as Node)
      ) {
        setShowReactionSelector(false);
      }
    };

    if (showReactionSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showReactionSelector]);

  // Close reaction selector when card is no longer visible/hovered
  useEffect(() => {
    if (!isVisible && !isLarge) {
      setShowReactionSelector(false);
    }
  }, [isVisible, isLarge]);

  if (!canEdit || (!isLarge && !isVisible)) {
    return null;
  }

  if (isLarge) {
    return (
      <div className='large-card-action-bar-container'>
        {/* <Tooltip
          title='Bookmark this place'
          arrow
          placement='top'
        >
          <IconButton
            sx={largeIconButtonSx}
            size='small'
            onClick={handleBookmarkClick}
          >
            <BookmarkBorderIcon />
          </IconButton>
        </Tooltip> */}

        <div
          className='reaction-button-container'
          ref={reactionContainerRef}
        >
          <Tooltip
            title='Add your reaction'
            arrow
            placement='top'
          >
            <IconButton
              className='card-action-bar-icon-button large'
              onClick={handleReactionClick}
            >
              {userCurrentReaction?.type !== undefined ? (
                <span className='reaction-emoji'>{getReactionEmoji(userCurrentReaction.type)}</span>
              ) : (
                <AddReactionOutlinedIcon />
              )}
            </IconButton>
          </Tooltip>

          {showReactionSelector && (
            <div className='large-card-reaction-selector'>
              <div className='card-action-bar-button-group'>
                {REACTION_TYPES.map((reactionType) => (
                  <Tooltip
                    key={reactionType}
                    title={getReactionDescription(reactionType)}
                    arrow
                    placement='top'
                  >
                    <button
                      type='button'
                      className={`large-card-reaction-button${isSelected(reactionType) ? ' selected' : ''}`}
                      onClick={(e) => handleReactionSelect(reactionType, e)}
                    >
                      {getReactionEmoji(reactionType)}
                    </button>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}
        </div>

        <ActionMenu
          card={card}
          tripId={tripId}
          canEdit={canEdit}
          isLarge={isLarge}
          onRename={onRename}
          onChangeCoverPhoto={onChangeCoverPhoto}
          onDelete={onDelete}
        />
      </div>
    );
  }

  const actionBarClassName = `action-bar-container${isVisible ? ' visible' : ''}`;

  return (
    <div className={actionBarClassName}>
      {/* <Tooltip
        title='Bookmark this place'
        arrow
        placement='top'
      >
        <IconButton
           className='card-action-bar-icon-button small'
          size='small'
          onClick={handleBookmarkClick}
        >
          <BookmarkBorderIcon />
        </IconButton>
      </Tooltip> */}

      <div
        className='reaction-button-container'
        ref={reactionContainerRef}
      >
        <Tooltip
          title='Add your reaction'
          arrow
          placement='top'
        >
          <IconButton onClick={handleReactionClick}>
            {userCurrentReaction?.type !== undefined ? (
              <span className='reaction-emoji'>{getReactionEmoji(userCurrentReaction.type)}</span>
            ) : (
              <AddReactionOutlinedIcon />
            )}
          </IconButton>
        </Tooltip>

        {showReactionSelector && (
          <div className='card-action-bar-reaction-selector'>
            <div className='card-action-bar-button-group'>
              {REACTION_TYPES.map((reactionType) => (
                <Tooltip
                  key={reactionType}
                  title={getReactionDescription(reactionType)}
                  arrow
                  placement='right'
                >
                  <button
                    type='button'
                    className={`card-action-bar-reaction-button${isSelected(reactionType) ? ' selected' : ''}`}
                    onClick={(e) => handleReactionSelect(reactionType, e)}
                  >
                    {getReactionEmoji(reactionType)}
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        )}
      </div>

      <ActionMenu
        card={card}
        tripId={tripId}
        canEdit={canEdit}
        onRename={onRename}
        onChangeCoverPhoto={onChangeCoverPhoto}
        onDelete={onDelete}
      />
    </div>
  );
}

CardActionBar.displayName = 'CardActionBar';
export default CardActionBar;
